import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { GlobalService } from './global.service';
import { map, catchError } from 'rxjs/operators'
import { environment } from 'src/environments/environment';
import { Subject, Observable } from 'rxjs';
import { LocalStorageService } from './local-storage.service';

const devUrl = environment.backendurl

@Injectable({
  providedIn: 'root'
})
export class TransactionService {

  constructor(private http: HttpClient, private localstorageservice: LocalStorageService) { }

  public fetchCurrentPlanDetailsData: Subject<any> = new Subject(); 

  /**
   * Refresh all plan & credits details of current user
   * @description
   * Generic function to fetch current user's plan details incl. credits and also update the header & stores with latest details
   * Call this method to automatically handle all plan & credits-related data refresh
   * @returns Observable resolving to object containing user's latest plan details
   */
  updateAllCreditsForCurrentUser() {
    return new Observable(observer => {
      const currentUserNumber = this.localstorageservice.getItem('usernumber');
      if (!currentUserNumber) {
        observer.next({});
        observer.complete();
        return;
      }
  
      this.fetchcurrentplandetails(currentUserNumber).pipe(
        map((res: any) => {
          if (res.status === 'success') {
            const subscriptionDetails: any = res?.data || {};

            subscriptionDetails.balancecontactenrichmentcredits = res.data.balancecontactenrichmentcredits;
            subscriptionDetails.balancebasiccontactenrichmentcredits = res.data.balancebasiccontactenrichmentcredits;

            if (subscriptionDetails.isteamadmin) {
              // Here the logged in person is a Team Admin
              subscriptionDetails.balancejdparsingcredits = res.data.pooledbalancejdparsingcredits;
              subscriptionDetails.balancesmartemailcredits = res.data.pooledbalancesmartemailcredits;
              subscriptionDetails.balancetalentpipelinecredits = res.data.pooledbalancetalentpipelinecredits;
              subscriptionDetails.balancebulkgrabbingcredits = res.data.pooledbalancebulkgrabbingcredits;
              subscriptionDetails.cepercentage = ((subscriptionDetails.balancecontactenrichmentcredits / subscriptionDetails.contactenrichmentcredits) * 100).toFixed(2)
            } else {
              if (subscriptionDetails.teamadmindetails) {
                // Here the logged in person is a Team Member
                subscriptionDetails.balancejdparsingcredits = res.data.teamadmindetails.adminbalancejdparsingcredits;
                subscriptionDetails.balancesmartemailcredits = res.data.teamadmindetails.adminbalancesmartemailcredits;
                subscriptionDetails.balancebulkgrabbingcredits = res.data.teamadmindetails.adminbalancebulkgrabbingcredits;
                subscriptionDetails.balancetalentpipelinecredits = res.data.teamadmindetails.adminbalancetalentpipelinecredits;
                subscriptionDetails.cepercentage = ((subscriptionDetails.balancecontactenrichmentcredits / subscriptionDetails.reservedcontactcreditscount) * 100).toFixed(2)
                subscriptionDetails.bcepercentage = ((subscriptionDetails.balancebasiccontactenrichmentcredits / subscriptionDetails.reservedbasiccontactcreditscount) * 100).toFixed(2)
                subscriptionDetails.contactenrichmentcredits = subscriptionDetails.reservedcontactcreditscount;
                subscriptionDetails.basiccontactenrichmentcredits = subscriptionDetails.reservedbasiccontactcreditscount;
                
                this.localstorageservice.setItem('isteamMember', true);
              } else {
                // Here the logged in person is a normal (who does not belong to any team)
                subscriptionDetails.balancejdparsingcredits = res.data.balancejdparsingcredits;
                subscriptionDetails.balancesmartemailcredits = res.data.balancesmartemailcredits;
                subscriptionDetails.balancetalentpipelinecredits = res.data.balancetalentpipelinecredits;
                subscriptionDetails.balancebulkgrabbingcredits = res.data.balancebulkgrabbingcredits;
                subscriptionDetails.cepercentage = ((subscriptionDetails.balancecontactenrichmentcredits / subscriptionDetails.contactenrichmentcredits) * 100).toFixed(2)
                
                this.localstorageservice.setItem('isteamMember', null);
              }
            }

            subscriptionDetails.jdpercentage = ((subscriptionDetails.balancejdparsingcredits / subscriptionDetails.jdparsingcredits) * 100).toFixed(2);
            subscriptionDetails.sepercentage = ((subscriptionDetails.balancesmartemailcredits / subscriptionDetails.smartemailcredits) * 100).toFixed(2);
            subscriptionDetails.grabpercentage = ((subscriptionDetails.balancebulkgrabbingcredits / subscriptionDetails.bulkgrabbingcredits) * 100).toFixed(2);
            subscriptionDetails.tppercentage = ((subscriptionDetails.balancetalentpipelinecredits / subscriptionDetails.talentpipelinecredits) * 100).toFixed(2);
            subscriptionDetails.overallpercentage = ((res.data.totalbalancecreditswithentireteam / subscriptionDetails.contactenrichmentcredits) * 100).toFixed(2);
            subscriptionDetails.overallbasicpercentage = ((res.data.totalbalancebasiccreditswithentireteam / subscriptionDetails.basiccontactenrichmentcredits) * 100).toFixed(2);
            subscriptionDetails.overallcredits = res.data.totalbalancecreditswithentireteam
            subscriptionDetails.overallbasiccredits = res.data.totalbalancebasiccreditswithentireteam
  
            // update in localstorage
            this.localstorageservice.setItem('paymentplanname',subscriptionDetails.paymentplanname);
            this.localstorageservice.setItem('plantype',subscriptionDetails.plantype);
            this.localstorageservice.setItem('jdcredits', subscriptionDetails.balancejdparsingcredits);
            this.localstorageservice.setItem('tpcredits', subscriptionDetails.balancetalentpipelinecredits);
            this.localstorageservice.setItem('cecredits', subscriptionDetails.balancecontactenrichmentcredits);
            this.localstorageservice.setItem('secredits', subscriptionDetails.balancesmartemailcredits);
            this.localstorageservice.setItem('grabcredits', subscriptionDetails.balancebulkgrabbingcredits);
            const userData = this.localstorageservice.getItem('userdata');
            userData['balancebasiccontactenrichmentcredits'] = subscriptionDetails.balancebasiccontactenrichmentcredits;
            this.localstorageservice.setItem('userdata', userData);
  
            // notify header to render latest data
            this.createPaymentData();
  
            observer.next(subscriptionDetails);
            observer.complete();
          }
        })
      ).subscribe();
    });
  }

  fetchcurrentplandetails(userid){
    let url = devUrl + "/paymentflow/fetchcurrentplandetails?usernumber="+userid
    return this.http.get(url).pipe(
      map(response => { return response; })
    , catchError(GlobalService.handleError('fetchcurrentplandetails', url)));
  }


  fetchpaymenttransactionhistory(userid,startindex){
    let url = devUrl + "/paymentflow/fetchpaymenttransactionhistory?usernumber="+userid+"&startindex="+startindex 
    return this.http.get(url).pipe(
      map(response => { return response; })
    , catchError(GlobalService.handleError('fetchcurrentplandetails', url)));
  }
  
  chaneplan(data){
    var url = devUrl + "/paymentflow/changeplanrequest"
    return this.http.post(url, data).pipe(map(response => { return response; })
      , catchError(GlobalService.handleError('chaneplan', url)));
  }

  fetchallactivepaymentplans(){
    let url = devUrl + "/paymentflow/fetchallactivepaymentplans"
    return this.http.get(url).pipe(
      map(response => { return response; })
    , catchError(GlobalService.handleError('fetchallactivepaymentplans', url)));
  }

  sendcreditexpirymail(data){
    let url = devUrl + "/paymentflow/sendcreditexpirymail"
    return this.http.post(url, data).pipe(map(response => { return response; })
      , catchError(GlobalService.handleError('chaneplan', url)));
  }

  fetchallstripeplans(){
    let url = devUrl + "/paymentflow/fetchallstripeplans"
    return this.http.get(url).pipe(map(response => { return response; })
      , catchError(GlobalService.handleError('fetchallstripeplans', url)));
  }

  createcustomer(data){
    let url = devUrl + "/stripe/createcustomer"
    return this.http.post(url,data).pipe(map(response => { return response; })
      , catchError(GlobalService.handleError('createcustomer', url)));
  }

  createcheckouturl(data){
    let url = devUrl + "/stripe/createcheckouturl"
    return this.http.post(url,data).pipe(map(response => { return response; })
      , catchError(GlobalService.handleError('createcheckouturl', url)));
  }

  verifypaymentstatus(data){
    let url = devUrl + "/stripe/verifypaymentstatus"
    return this.http.post(url,data).pipe(map(response => { return response; })
      , catchError(GlobalService.handleError('verifypaymentstatus', url)));
  }

  cancelsubscription(data){
    let url = devUrl + "/stripe/cancelsubscription"
    return this.http.post(url,data).pipe(map(response => { return response; })
      , catchError(GlobalService.handleError('verifypaymentstatus', url)));
  }

  fetchplan(data){
    let url = devUrl + "/paymentflow/fetchplan?paymentplanid=" + data
    return this.http.get(url).pipe(map(response => { return response; })
      , catchError(GlobalService.handleError('verifypaymentstatus', url)));
  }

  
public createPaymentData (): void {
  this.fetchCurrentPlanDetailsData.next();
}

public getPaymentData (): Observable<any> {
  return this.fetchCurrentPlanDetailsData.asObservable();
}

}
